import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';
import imageCompression from 'browser-image-compression';

interface UploadOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  onProgress?: (progress: number) => void;
}

export const optimizeAndUploadImage = async (
  file: File,
  path: string,
  options: UploadOptions = {}
) => {
  const {
    maxSizeMB = 1,
    maxWidthOrHeight = 1920,
    useWebWorker = true,
    onProgress
  } = options;

  try {
    // Check if file is already compressed enough
    if (file.size <= maxSizeMB * 1024 * 1024) {
      console.log('File already optimized, skipping compression');
      return uploadFile(file, path, onProgress);
    }

    // Determine optimal compression settings based on file size
    const initialQuality = file.size > 5 * 1024 * 1024 ? 0.6 : 0.8;

    // Compress image with more resilient settings
    const compressedFile = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker,
      initialQuality,
      alwaysKeepResolution: true,
      fileType: 'image/jpeg', // Force JPEG for better compatibility
      onProgress: (progress) => {
        if (onProgress) onProgress(progress * 0.5); // First 50% is compression
      }
    });

    return uploadFile(compressedFile, path, (progress) => {
      if (onProgress) onProgress(50 + progress * 0.5); // Last 50% is upload
    });

  } catch (error) {
    console.error('Error in image optimization:', error);
    // Fallback to direct upload if compression fails
    console.log('Compression failed, attempting direct upload');
    return uploadFile(file, path, onProgress);
  }
};

const uploadFile = async (
  file: File | Blob,
  path: string,
  onProgress?: (progress: number) => void
): Promise<string> => {
  // Generate unique filename
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(7);
  const extension = (file instanceof File ? file.name : 'image.jpg').split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `${timestamp}_${randomString}.${extension}`;
  const storageRef = ref(storage, `${path}/${fileName}`);

  // Set proper metadata
  const metadata = {
    contentType: file instanceof File ? file.type : 'image/jpeg',
    cacheControl: 'public, max-age=31536000',
  };

  // Create upload task
  const uploadTask = uploadBytesResumable(storageRef, file, metadata);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) onProgress(progress);
      },
      (error) => {
        console.error('Upload error:', error);
        reject(new Error('Error uploading file: ' + error.message));
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        } catch (error) {
          console.error('Error getting download URL:', error);
          reject(error);
        }
      }
    );
  });
};

// Helper function to get optimized image URL
export const getOptimizedImageUrl = (url: string, width?: number) => {
  if (!url) return url;
  
  try {
    const imageUrl = new URL(url);
    
    // Add optimization parameters
    if (width) {
      imageUrl.searchParams.append('width', width.toString());
    }
    imageUrl.searchParams.append('quality', '80');
    imageUrl.searchParams.append('auto', 'compress,format');
    
    return imageUrl.toString();
  } catch {
    return url;
  }
};